/**
 * ToolCallCard Component
 * Displays a single tool invocation as a slim card inline in the agent's message.
 */

import { Box, Typography, Tooltip } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import {
  Storage as StorageIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  Slideshow as SlideshowIcon,
  MenuBook as MenuBookIcon,
  Gavel as GavelIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

const TOOL_META = {
  query_database: {
    pendingLabel: 'Querying database',
    doneLabel: 'Queried database',
    icon: <StorageIcon sx={{ fontSize: 14 }} />,
    argsKey: 'sql_query',
  },
  create_visualization: {
    pendingLabel: 'Creating visualization',
    doneLabel: 'Created visualization',
    errorLabel: 'Retrying visualization',
    icon: <BarChartIcon sx={{ fontSize: 14 }} />,
  },
  generate_excel: {
    pendingLabel: 'Generating Excel report',
    doneLabel: 'Generated Excel report',
    icon: <TableChartIcon sx={{ fontSize: 14 }} />,
  },
  generate_ppt: {
    pendingLabel: 'Building presentation',
    doneLabel: 'Built presentation',
    icon: <SlideshowIcon sx={{ fontSize: 14 }} />,
  },
  query_knowledge_base: {
    pendingLabel: 'Searching knowledge base',
    doneLabel: 'Searched knowledge base',
    icon: <MenuBookIcon sx={{ fontSize: 14 }} />,
    argsKey: 'search_query',
  },
  get_congressional_justification_info: {
    pendingLabel: 'Searching congressional justifications',
    doneLabel: 'Searched congressional justifications',
    icon: <GavelIcon sx={{ fontSize: 14 }} />,
    argsKey: 'search_query',
  },
  query_va_budget_policy: {
    pendingLabel: 'Searching Agency Budget Documentation',
    doneLabel: 'Searched Agency Budget Documentation',
    icon: <LanguageIcon sx={{ fontSize: 14 }} />,
    argsKey: 'search_query',
  },
};

const StatusIndicator = ({ status, isVizError }) => {
  if (status === 'pending') {
    return <LoadingIndicator variant="spinner" size={12} color="#2C365E" />;
  }
  if (status === 'error') {
    return isVizError
      ? <ErrorIcon sx={{ fontSize: 14, color: '#c49a1a', flexShrink: 0 }} />
      : <ErrorIcon sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }} />;
  }
  return <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />;
};

const ToolCallCard = ({ toolCall }) => {
  const { tool, status, summary, args } = toolCall;
  const meta = TOOL_META[tool] || {
    pendingLabel: tool,
    doneLabel: tool,
    icon: <BuildIcon sx={{ fontSize: 14 }} />,
  };

  const label = status === 'pending' ? meta.pendingLabel
    : status === 'error' ? (meta.errorLabel || meta.doneLabel)
    : meta.doneLabel;

  const isVizError = status === 'error' && tool === 'create_visualization';

  // Build tooltip: query/search term when available, summary when done
  const queryTerm = meta.argsKey && args?.[meta.argsKey];
  let tooltipContent = '';
  if (status === 'done' && summary) {
    tooltipContent = queryTerm ? `${summary}\n\n${queryTerm}` : summary;
  } else if (queryTerm) {
    tooltipContent = queryTerm;
  }
  // Friendly hover message for viz errors
  if (status === 'error' && !tooltipContent) {
    tooltipContent = tool === 'create_visualization'
      ? 'Guru ran into an issue generating your visual and will try again.'
      : summary || '';
  }
  const hasTooltip = Boolean(tooltipContent);

  return (
    <Tooltip
      title={hasTooltip ? (
        <Box sx={{ maxWidth: 340, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
          {tooltipContent}
        </Box>
      ) : ''}
      placement="top"
      arrow
      disableHoverListener={!hasTooltip}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.4,
          borderRadius: '6px',
          border: '1px solid',
          borderColor: status === 'error'
            ? (isVizError ? '#e0c87a' : 'error.light')
            : '#dde3ed',
          backgroundColor: status === 'error'
            ? (isVizError ? '#fef9eb' : '#fff5f5')
            : '#f5f7fa',
          color: status === 'error'
            ? (isVizError ? '#8a6d1b' : 'error.dark')
            : '#3d4a63',
          transition: 'border-color 0.2s',
          '&:hover': hasTooltip ? { borderColor: '#aab4c8', cursor: 'default' } : {},
        }}
      >
        <Box sx={{ color: '#2C365E', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {meta.icon}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, fontSize: '0.72rem', lineHeight: 1, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
        <StatusIndicator status={status} isVizError={isVizError} />
      </Box>
    </Tooltip>
  );
};

export default ToolCallCard;
